import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, euro, fmtDate, openAuthPdf } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { PlusIcon as Plus, TrashIcon as Trash, FilePdfIcon as FilePdf, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown, CaretUpDownIcon as CaretUpDown } from "@phosphor-icons/react";

const ivSortVal = (i, k) => {
  switch (k) {
    case "numero": return (i.numero || "").toLowerCase();
    case "data": return i.data_inizio || "";
    case "cliente": return (i.cliente_nome || "").toLowerCase();
    case "tecnico": return (i.tecnico_nome || "").toLowerCase();
    case "richiesta": return (i.richiesta_intervento || i.titolo || "").toLowerCase();
    case "ore": return i.tempo_totale_minuti || 0;
    case "stato": return i.stato || "";
    default: return "";
  }
};

const SC_IV_STATO = {
  da_pianificare: { l: "Da pianificare", c: "bg-amber-100 text-amber-800" },
  pianificata: { l: "Fissato appuntamento", c: "bg-blue-100 text-blue-800" },
  in_corso: { l: "In lavorazione", c: "bg-violet-100 text-violet-800" },
  completata: { l: "Completata", c: "bg-green-100 text-green-800" },
  sospesa: { l: "Sospesa", c: "bg-orange-100 text-orange-800" },
  annullata: { l: "Annullata", c: "bg-zinc-100 text-zinc-500" },
};

export default function AssistenzaInformaticaReport() {
  const navigate = useNavigate();
  const [interventions, setInterventions] = useState([]);
  const [techs, setTechs] = useState([]);
  const [ivSort, setIvSort] = useState({ key: null, dir: "asc" });
  const ivToggle = (k) => setIvSort((s) => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" });

  const load = useCallback(() => {
    api.get("/attivita").then(({ data }) => setInterventions(data)).catch(() => setInterventions([]));
    api.get("/analytics/technicians").then(({ data }) => setTechs(data.tecnici)).catch(() => setTechs([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const delIv = async (id) => { if (!window.confirm("Eliminare l'intervento?")) return; await api.delete(`/attivita/${id}`); toast.success("Eliminato"); load(); };

  const ivSorted = (() => {
    const arr = [...(interventions || [])];
    if (ivSort.key) { const dir = ivSort.dir === "asc" ? 1 : -1; arr.sort((a, b) => { const va = ivSortVal(a, ivSort.key), vb = ivSortVal(b, ivSort.key); if (va < vb) return -1 * dir; if (va > vb) return 1 * dir; return 0; }); }
    return arr;
  })();
  const IvHead = ({ k, children, className }) => (
    <TableHead className={className}>
      <button type="button" onClick={() => ivToggle(k)} className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`iv-sort-${k}`}>
        {children}
        {ivSort.key === k ? (ivSort.dir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />) : <CaretUpDown size={12} className="opacity-40" />}
      </button>
    </TableHead>
  );

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader title="Assistenza Informatica" subtitle="Interventi e rendimento tecnici dell'assistenza informatica" />
        <Tabs defaultValue="interventi">
          <TabsList className="rounded-sm mb-4">
            <TabsTrigger value="interventi" data-testid="tab-iv">Interventi</TabsTrigger>
            <TabsTrigger value="tecnici" data-testid="tab-tech">Rendimento tecnici</TabsTrigger>
          </TabsList>

          {/* Interventions */}
          <TabsContent value="interventi">
            <div className="flex justify-end gap-2 mb-3">
              <Button data-testid="new-iv-btn" onClick={() => navigate("/interventi/nuovo")} className="rounded-sm gap-2"><Plus size={16} weight="bold" /> Nuovo intervento</Button>
            </div>
            <Card className="rounded-sm border-border shadow-none">
              <Table>
                <TableHeader><TableRow>
                  <IvHead k="numero">Numero</IvHead><IvHead k="data">Data</IvHead><IvHead k="cliente">Cliente</IvHead><IvHead k="tecnico">Tecnico/i</IvHead>
                  <IvHead k="richiesta">Richiesta</IvHead><IvHead k="ore" className="text-right">Ore</IvHead><IvHead k="stato">Stato</IvHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {ivSorted.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Nessun intervento.</TableCell></TableRow>}
                  {ivSorted.map((i) => {
                    const sm = SC_IV_STATO[i.stato] || { l: i.stato, c: "bg-zinc-100 text-zinc-600" };
                    const ore = ((i.tempo_totale_minuti || 0) / 60);
                    return (
                      <TableRow key={i.id} data-testid="iv-row" className="hover:bg-secondary cursor-pointer" onClick={() => navigate(`/interventi/${i.id}`)}>
                        <TableCell className="font-mono text-xs">{i.numero || "—"}</TableCell>
                        <TableCell className="tabular-nums">{fmtDate(i.data_inizio)}</TableCell>
                        <TableCell className="text-muted-foreground">{i.cliente_nome}</TableCell>
                        <TableCell className="text-muted-foreground">{i.tecnico_nome || "—"}</TableCell>
                        <TableCell className="max-w-xs truncate">{i.richiesta_intervento || i.titolo}</TableCell>
                        <TableCell className="text-right tabular-nums">{ore ? `${ore.toFixed(2)}h` : "—"}</TableCell>
                        <TableCell><Badge variant="outline" className={`rounded-sm text-[10px] ${sm.c}`}>{sm.l}</Badge></TableCell>
                        <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title="Apri rapportino PDF" onClick={() => openAuthPdf(`/attivita/${i.id}/rapportino.pdf`)}><FilePdf size={15} /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => delIv(i.id)}><Trash size={14} /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Technicians */}
          <TabsContent value="tecnici">
            <Card className="rounded-sm border-border shadow-none p-6 mb-6">
              <h3 className="font-heading font-semibold mb-4">Interventi e ore per tecnico</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={techs}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="tecnico" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#09090B", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 }} />
                  <Bar dataKey="ore_totali" name="Ore totali" fill="#00396a" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="ore_fatturabili" name="Ore fatturabili" fill="#4aa3df" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="rounded-sm border-border shadow-none">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Tecnico</TableHead><TableHead className="text-right">Interventi</TableHead><TableHead className="text-right">Ore totali</TableHead>
                  <TableHead className="text-right">Ore fatturabili</TableHead><TableHead className="text-right">Utilizzo %</TableHead>
                  <TableHead className="text-right">Ricavi</TableHead><TableHead className="text-right">Ticket assegnati</TableHead><TableHead className="text-right">Ticket risolti</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {techs.map((t, i) => (
                    <TableRow key={i} data-testid="tech-row" className="hover:bg-secondary">
                      <TableCell className="font-medium">{t.tecnico}</TableCell>
                      <TableCell className="text-right tabular-nums">{t.interventi}</TableCell>
                      <TableCell className="text-right tabular-nums">{t.ore_totali}h</TableCell>
                      <TableCell className="text-right tabular-nums">{t.ore_fatturabili}h</TableCell>
                      <TableCell className="text-right tabular-nums">{t.utilizzo_pct}%</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{euro(t.ricavi)}</TableCell>
                      <TableCell className="text-right tabular-nums">{t.ticket_assegnati}</TableCell>
                      <TableCell className="text-right tabular-nums">{t.ticket_risolti}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </InternalLayout>
  );
}
